import React, { useState, useEffect } from 'react'
import { Button, TextInput, SelectDropDown, SwitchBox, Typography, Label } from 'procyon-ui'
import { FullScreenModal, LoadingFeedback } from 'V2Components'
import { reduxApiClient } from 'infra'
import { enqueueNotification } from 'Utils/Helpers'
import { cn } from 'Utils/Helpers'
import useMultiSlice from 'Core/Hooks/useMultiSlice'

export const AddGitServerModal = ({ onClose, server }) => {
  const isEdit = !!server
  const [name, setName] = useState(server?.ObjectMeta?.Name || '')
  const [type, setType] = useState(server?.Spec?.ConnType || 'Github')
  const [isLoading, setIsLoading] = useState(false)
  const { slices } = useMultiSlice(['serverList'])

  const getErrors = () => {
    const nameRegex = /^[a-zA-Z0-9_-]+$/ // don't allow space
    if (!name.trim()) return 'Name is required.'
    if (!nameRegex.test(name)) return 'Name must not contain spaces, dots, or special characters.'
    if (
      !isEdit &&
      slices.serverList.some(
        (server) => server.ObjectMeta.Name.toLowerCase() === name.toLowerCase()
      )
    ) {
      return 'This name is already in use.'
    }
  }

  const formErrors = getErrors()

  const handleSave = async () => {
    setIsLoading(true)

    const formPayload = isEdit
      ? {
          ...server,
          ObjectMeta: { ...server.ObjectMeta, Name: name },
          Spec: { ...server.Spec, ConnType: type, DisplayName: name, Name: name }
        }
      : {
          ObjectMeta: {
            Kind: 'Server',
            Name: name
          },
          Spec: {
            InstanceId: name,
            PrivateIP: type === 'Github' ? 'public' : 'private',
            PublicIP: type === 'Github' ? 'github.com' : 'gitlab.com',
            State: 'managed',
            Type: 'remote',
            ServerPort: 22,
            Name: name,
            DisplayName: name,
            ConnType: type
          }
        }
    try {
      if (isEdit) {
        await reduxApiClient('servers').update(formPayload)
        enqueueNotification('Successfully updated Git Server!', 'info')
      } else {
        await reduxApiClient('servers').create(formPayload)
        enqueueNotification('Successfully created Git Server!', 'info')
      }
      onClose()
    } catch (e) {
      console.error('Failed to save Git Server', e)
      enqueueNotification(
        `Failed to ${isEdit ? 'update' : 'create'} Git Server: ${e.message}`,
        'error'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onNameChange = (value) => {
    const sanitizedValue = value.replace(/[^a-zA-Z0-9_-]/g, '')
    setName(sanitizedValue)
  }

  return (
    <FullScreenModal showModal={true}>
      <div className='flex justify-center items-center h-full bg-[#2229455a]'>
        <div className='w-[626px] rounded-lg p-5 bg-white'>
          <Typography variant='h3'>{isEdit ? 'Edit' : 'Add'} Git Server</Typography>

          <TextInput
            label='Server Name'
            value={name}
            sx={{ width: '100%', marginTop: '10px' }}
            onChange={(e) => onNameChange(e.target.value)}
          />

          <SelectDropDown
            label='Type'
            value={type}
            onChange={(e) => setType(e.target.value)}
            menuItems={[
              { label: 'GitHub', value: 'Github' },
              { label: 'GitLab', value: 'Gitlab' }
            ]}
          />

          <div
            className={cn('mt-8 flex items-center gap-8', {
              'justify-between': formErrors,
              'justify-end': !formErrors
            })}
          >
            {formErrors && <Label variant='warning' text={formErrors} />}
            <div className='flex justify-end gap-2 mt-4'>
              <Button variant='gray' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!!formErrors || isLoading}>
                {isEdit ? 'Save' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <LoadingFeedback
        caption='Please wait..'
        loading={isLoading}
        message={`${isEdit ? 'Updating' : 'Creating'} Git Server`}
      />
    </FullScreenModal>
  )
}
